import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  useGetVersionsQuery,
  useAllCommentsGenericQuery,
  useLogEntriesByReferenceQuery,
  LogCategory,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { selectedDate as selectedDateAtom, selectedWorkpackIdForKpiTracker } from '../state';
import { formatDateToYYYYMMDD } from '../utils';
import { COLLECTION_NAMES } from '@/constants';
import { KPI_NOTE_LOG_METADATA_KEY } from '../constants';

const WORKPACK_COLLECTION_NAME = 'Workpack';
const LOG_ENTRY_COLLECTION_NAME = 'LogEntry';

export const useKpiHistoryData = () => {
  const structureId = useGetStructureId();
  const workpackId = useAtomValue(selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(selectedDateAtom);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatDateToYYYYMMDD(selectedDate);

  // Fetch log entry IDs for this workpack
  const { data: logEntriesData, loading: logEntriesLoading } = useLogEntriesByReferenceQuery({
    variables: {
      input: {
        structureId,
        referenceId: workpackId || '',
        category: LogCategory.Worklog,
      },
    },
    skip: !structureId || !workpackId,
    fetchPolicy: 'cache-first',
  });

  // Extract log entry IDs
  const logEntryIds = useMemo(() => {
    return logEntriesData?.logEntriesByReference?.map((entry) => entry.id) ?? [];
  }, [logEntriesData?.logEntriesByReference]);

  // Fetch workpack versions (attachments)
  const {
    data: workpackVersionsData,
    loading: workpackLoading,
    error: workpackError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName: WORKPACK_COLLECTION_NAME,
        referenceId: workpackId,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip: !structureId || !workpackId,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch log entry versions using the fetched log entry IDs
  const {
    data: logEntryVersionsData,
    loading: logEntryVersionsLoading,
    error: logEntryError,
  } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName: LOG_ENTRY_COLLECTION_NAME,
        referenceIds: logEntryIds.length > 0 ? logEntryIds : undefined,
        logDate: formattedDate,
        timezone: userTimezone,
      },
    },
    skip: !structureId || !workpackId || logEntryIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch comments
  const { data: commentsData, loading: commentsLoading } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName: COLLECTION_NAMES.WORKPACK,
        referenceId: workpackId as string,
        structureId,
        metadata: {
          [KPI_NOTE_LOG_METADATA_KEY]: formattedDate,
        },
      },
    },
    skip: !structureId || !workpackId,
    fetchPolicy: 'cache-and-network',
  });

  const loading =
    workpackLoading || logEntriesLoading || logEntryVersionsLoading || commentsLoading;
  const error = workpackError || logEntryError;

  return {
    workpackVersionsData,
    logEntryVersionsData,
    commentsData,
    logEntryIds,
    loading,
    error,
  };
};
